import axios from 'axios';

// Configure axios to include credentials
axios.defaults.withCredentials = true;

// Use the proxy instead of the full URL
const API_URL = '/api';

// Get all field mappings
export const getFieldMappings = async () => {
  try {
    const response = await axios.get(`${API_URL}/field-mappings`);
    return response.data;
  } catch (error) {
    console.error('Error fetching field mappings:', error);
    throw error;
  }
};

// Save field mappings
export const saveFieldMappings = async (mappings) => {
  try {
    // Ensure we're only sending the necessary fields to the server
    const simplifiedMappings = mappings.map(({ opportunityField, salesforceField, salesforceObject }) => ({
      opportunityField,
      salesforceField,
      salesforceObject
    }));
    
    const response = await axios.post(`${API_URL}/field-mappings`, { 
      mappings: simplifiedMappings 
    });
    
    return response.data;
  } catch (error) {
    console.error('Error saving field mappings:', error);
    throw error;
  }
};

// Test field mappings with Salesforce
export const testFieldMappings = async (mappings) => {
  try {
    console.log('Testing field mappings:', mappings);
    
    // Ensure we're only sending the necessary fields to the server
    const simplifiedMappings = mappings.map(({ opportunityField, salesforceField, salesforceObject }) => ({
      opportunityField,
      salesforceField,
      salesforceObject
    }));
    
    const response = await axios.post(`${API_URL}/field-mappings/test`, { 
      mappings: simplifiedMappings 
    });
    
    return response.data;
  } catch (error) {
    console.error('Error testing field mappings:', error);
    throw error;
  }
};

// Get fields for a specific Salesforce object
export const getSalesforceObjectFields = async (objectName) => {
  try {
    const response = await axios.get(`${API_URL}/field-mappings/object-fields/${objectName}`);
    
    // Sort fields by label for easier selection
    const sortedFields = response.data.sort((a, b) => 
      a.label.localeCompare(b.label)
    );
    
    return sortedFields;
  } catch (error) {
    console.error(`Error fetching ${objectName} fields:`, error);
    throw error;
  }
};

// Get opportunity fields - for compatibility with existing code
export const getOpportunityFields = async () => {
  try {
    // Instead of fetching from API, return hardcoded form fields
    // This ensures the ContactDetail page keeps its original field structure
    return [
      // Business
      { name: 'BusinessLegalName', label: 'Business Legal Name', type: 'string', category: 'Business' },
      { name: 'DBA', label: 'DBA', type: 'string', category: 'Business' },
      { name: 'Industry', label: 'Industry', type: 'string', category: 'Business' },
      { name: 'EntityType', label: 'Entity Type', type: 'string', category: 'Business' },
      { name: 'FederalTaxID', label: 'Federal Tax ID', type: 'string', category: 'Business' },
      { name: 'AnnualRevenue', label: 'Annual Revenue', type: 'string', category: 'Business' },
      { name: 'AverageMonthlySales', label: 'Average Monthly Sales', type: 'string', category: 'Business' },
      { name: 'AverageNSFs', label: 'Average NSFs', type: 'number', category: 'Business' },
      { name: 'AverageNumberOfDeposits', label: 'Average Number of Deposits', type: 'number', category: 'Business' },
      { name: 'RequestedAmount', label: 'Requested Amount', type: 'string', category: 'Business' },
      { name: 'AverageBankBalance', label: 'Average Bank Balance', type: 'string', category: 'Business' },
      { name: 'BusinessStartDate', label: 'Business Start Date', type: 'date', category: 'Business' },
      { name: 'SICCode', label: 'SIC Code', type: 'string', category: 'Business' },
      
      // Business Address
      { name: 'BusinessStreet', label: 'Street', type: 'string', category: 'Business Address' },
      { name: 'BusinessCity', label: 'City', type: 'string', category: 'Business Address' },
      { name: 'BusinessState', label: 'State', type: 'string', category: 'Business Address' },
      { name: 'BusinessZipCode', label: 'Zip Code', type: 'string', category: 'Business Address' },
      
      // Primary Contact
      { name: 'ContactFirstName', label: 'First Name', type: 'string', category: 'Primary Contact' },
      { name: 'ContactLastName', label: 'Last Name', type: 'string', category: 'Primary Contact' },
      { name: 'ContactTitle', label: 'Title', type: 'string', category: 'Primary Contact' },
      { name: 'ContactSSN', label: 'SSN', type: 'string', category: 'Primary Contact' },
      { name: 'ContactDateOfBirth', label: 'Date of Birth', type: 'date', category: 'Primary Contact' },
      { name: 'ContactEmail', label: 'Email', type: 'string', category: 'Primary Contact' },
      { name: 'ContactCellPhone', label: 'Cell Phone', type: 'string', category: 'Primary Contact' },
      { name: 'ContactOwnershipPercentage', label: 'Ownership Percentage', type: 'string', category: 'Primary Contact' },
      { name: 'ContactCreditScore', label: 'Credit Score', type: 'string', category: 'Primary Contact' },
      { name: 'CumulativeOwnershipPercentage', label: 'Cumulative Ownership Percentage', type: 'number', category: 'Primary Contact' },
      
      // Contact Address
      { name: 'ContactStreet', label: 'Street', type: 'string', category: 'Contact Address' },
      { name: 'ContactCity', label: 'City', type: 'string', category: 'Contact Address' },
      { name: 'ContactState', label: 'State', type: 'string', category: 'Contact Address' },
      { name: 'ContactZipCode', label: 'Zip Code', type: 'string', category: 'Contact Address' }
    ];
  } catch (error) {
    console.error('Error fetching opportunity fields:', error);
    throw error;
  }
};

// Get account fields
export const getAccountFields = async () => {
  try {
    const response = await axios.get(`${API_URL}/field-mappings/object-fields/Account`);
    return response.data;
  } catch (error) {
    console.error('Error fetching account fields:', error);
    throw error;
  }
};

// Get contact fields
export const getContactFields = async () => {
  try {
    const response = await axios.get(`${API_URL}/field-mappings/object-fields/Contact`);
    return response.data;
  } catch (error) {
    console.error('Error fetching contact fields:', error);
    throw error;
  }
}; 